import { NSARouterMakerResult } from "namirasoft-account-react";
import { RunHelper, RunRow, RunStatus } from "namirasoft-expert";
import { MoneyFormatter, NSCopy } from "namirasoft-site-react";
import { Children, Component, createRef, ReactNode, RefObject } from "react";
import { ExpertRouterProps } from "../ExpertRouterProps";
import ChatMessage from "./ChatMessage";
import Duration from "./Duration";
import Styles from "./RunLayout.module.css";

// todo design and change images
const StatusIcons: Record<string, ReactNode> = {
    [RunStatus.Pending]: (
        <svg className={Styles.ns_run_status_icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
        </svg>
    ),
    [RunStatus.Running]: (
        <svg className={`${Styles.ns_run_status_icon} ${Styles.ns_run_status_icon_spin}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
    ),
    [RunStatus.Waiting]: (
        <svg className={Styles.ns_run_status_icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    ),
    [RunStatus.Successful]: (
        <svg className={Styles.ns_run_status_icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="9 12 11.5 14.5 16 10" />
        </svg>
    ),
    [RunStatus.Failed]: (
        <svg className={Styles.ns_run_status_icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
    ),
};

interface RunLayoutProps extends NSARouterMakerResult, ExpertRouterProps
{
    run: RunRow;
    children?: ReactNode;
    defer?: boolean;
    onLoadDetails?: () => void;
    onRunChanged?: (run: RunRow) => void;
}

interface RunLayoutState
{
    run: RunRow;
}

class RunLayout extends Component<RunLayoutProps, RunLayoutState>
{
    private static readonly BOTTOM_FOLLOW_THRESHOLD = 80;

    private pollTimer: ReturnType<typeof setTimeout> | null = null;
    private pollInFlight = false;
    private unmounted = false;
    private currentHash: number = 0;
    private containerRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();
    private bottomRef: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();
    private resizeObserver: ResizeObserver | null = null;
    private prevHeight: number = 0;
    private wasAtBottom: boolean = true;

    constructor(props: RunLayoutProps)
    {
        super(props);
        this.state = { run: props.run };
        this.currentHash = this.hashRun(props.run);
    }

    override componentDidMount(): void
    {
        let el = this.containerRef.current;
        if (el && typeof ResizeObserver !== "undefined")
        {
            this.prevHeight = el.offsetHeight;
            this.wasAtBottom = this.isUserNearBottom();
            window.addEventListener("scroll", this.handleWindowScroll, { passive: true });
            this.resizeObserver = new ResizeObserver(() =>
            {
                let h = el.offsetHeight;
                if (h > this.prevHeight && this.wasAtBottom)
                    this.bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
                this.prevHeight = h;
            });
            this.resizeObserver.observe(el);
        }
        this.schedulePollIfNeeded();
    }

    override componentDidUpdate(prevProps: RunLayoutProps): void
    {
        if (prevProps.run !== this.props.run && this.props.run.id !== this.state.run.id)
        {
            this.currentHash = this.hashRun(this.props.run);
            this.setState({ run: this.props.run }, () => this.schedulePollIfNeeded());
        }
    }

    override componentWillUnmount(): void
    {
        this.unmounted = true;
        this.clearPollTimer();
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        window.removeEventListener("scroll", this.handleWindowScroll);
    }

    private handleWindowScroll = (): void =>
    {
        this.wasAtBottom = this.isUserNearBottom();
    };

    private isUserNearBottom(): boolean
    {
        let scrollY = window.scrollY ?? window.pageYOffset ?? 0;
        let viewport = window.innerHeight;
        let docHeight = Math.max(
            document.documentElement.scrollHeight,
            document.body.scrollHeight,
        );
        return docHeight - (scrollY + viewport) <= RunLayout.BOTTOM_FOLLOW_THRESHOLD;
    }

    private clearPollTimer(): void
    {
        if (this.pollTimer !== null)
        {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    private schedulePollIfNeeded(): void
    {
        this.clearPollTimer();
        if (this.unmounted)
            return;
        if (RunHelper.isTerminalStatus(this.state.run.status))
            return;
        this.pollTimer = setTimeout(() => this.pollRun(), 1000);
    }

    private hashRun(run: RunRow): number
    {
        let str = JSON.stringify(run);
        let hash = 5381;
        for (let i = 0; i < str.length; i++)
            hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
        return hash;
    }

    private async pollRun(): Promise<void>
    {
        if (this.unmounted || this.pollInFlight)
            return;
        this.pollInFlight = true;
        try
        {
            if (this.unmounted)
                return;
            let run = await this.props.servers.expert.run.Get(this.props.run.id);
            if (run)
            {
                let newHash = this.hashRun(run);
                if (newHash !== this.currentHash)
                {
                    this.currentHash = newHash;
                    this.setState({ run }, () =>
                    {
                        this.schedulePollIfNeeded();
                        this.props.onRunChanged?.(run);
                    });
                }
                else
                    this.schedulePollIfNeeded();
            }
            else
                this.schedulePollIfNeeded();
        }
        catch
        {
            if (!this.unmounted)
                this.schedulePollIfNeeded();
        }
        finally
        {
            this.pollInFlight = false;
        }
    }

    private formatDate(date: Date): string
    {
        let d = new Date(date);
        return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
    }

    private formatCost(value: number): string
    {
        return new MoneyFormatter().format(value);
    }

    override render()
    {
        let { children } = this.props;
        let { run } = this.state;

        return (
            <div className={Styles.ns_run} ref={this.containerRef}>

                <div className={Styles.ns_run_footer}>
                    <span className={Styles.ns_run_footer_item}>
                        <span className={Styles.ns_run_footer_label}>Run ID:</span>
                        <span><NSCopy>{run.id}</NSCopy></span>
                    </span>

                    <span className={Styles.ns_run_footer_item}>
                        <span className={Styles.ns_run_footer_label}>Created:</span>
                        <span>{this.formatDate(run.created_at)}</span>
                    </span>

                    <span className={Styles.ns_run_footer_item}>
                        <span className={Styles.ns_run_footer_label}>Duration:</span>
                        <span><Duration started_at={run.started_at} finished_at={run.finished_at} /></span>
                    </span>

                    <span className={Styles.ns_run_footer_item}>
                        <span className={Styles.ns_run_footer_label}>Status:</span>
                        <span className={`${Styles.ns_run_footer_status} ${Styles[run.status]}`}>
                            {StatusIcons[run.status]}
                            {run.status}
                        </span>
                    </span>

                    <span className={Styles.ns_run_footer_spacer} />

                    <span className={`${Styles.ns_run_footer_item} ${Styles.ns_run_footer_item_bold}`}>
                        <span className={Styles.ns_run_footer_label}>Total Tokens:</span>
                        <span>{run.total_token.toLocaleString()}</span>
                    </span>
                    <span className={Styles.ns_run_footer_separator}>=</span>
                    <span className={Styles.ns_run_footer_item}>
                        <span className={Styles.ns_run_footer_label}>Input:</span>
                        <span>{run.total_token_input.toLocaleString()}</span>
                    </span>
                    <span className={Styles.ns_run_footer_separator}>+</span>
                    <span className={Styles.ns_run_footer_item}>
                        <span className={Styles.ns_run_footer_label}>Output:</span>
                        <span>{run.total_token_output.toLocaleString()}</span>
                    </span>

                    <span className={`${Styles.ns_run_footer_item} ${Styles.ns_run_footer_item_bold}`}>
                        <span className={Styles.ns_run_footer_label}>Total Cost:</span>
                        <span>{this.formatCost(run.total_cost)}</span>
                    </span>
                    <span className={Styles.ns_run_footer_separator}>=</span>
                    <span className={Styles.ns_run_footer_item}>
                        <span className={Styles.ns_run_footer_label}>Input:</span>
                        <span>{this.formatCost(run.total_cost_input)}</span>
                    </span>
                    <span className={Styles.ns_run_footer_separator}>+</span>
                    <span className={Styles.ns_run_footer_item}>
                        <span className={Styles.ns_run_footer_label}>Output:</span>
                        <span>{this.formatCost(run.total_cost_output)}</span>
                    </span>
                </div>
                {
                    run.message_user &&
                    <ChatMessage
                        title="You:"
                        message={run.message_user}
                        markdown={false}
                    />
                }

                {
                    run.message_error &&
                    <div className={Styles.ns_run_error}>
                        {run.message_error}
                    </div>
                }

                {
                    Children.toArray(children).length > 0 &&
                    <div className={Styles.ns_run_body}>
                        {children}
                    </div>
                }

                {
                    run.message_ai &&
                    <ChatMessage
                        title="AI Response"
                        message={run.message_ai}
                        markdown={true}
                        align="left"
                        titleRight={
                            this.props.defer && this.props.onLoadDetails
                                ? (
                                    <button
                                        type="button"
                                        className={Styles.ns_run_load_details}
                                        onClick={this.props.onLoadDetails}
                                    >
                                        Load details
                                    </button>
                                )
                                : undefined
                        }
                    />
                }

                <div ref={this.bottomRef} />
            </div>
        );
    }
}

export default RunLayout;